import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { ThemeProvider, useTheme } from './contexts/ThemeContext.jsx';
import { NotificationsProvider } from './contexts/NotificationsContext.jsx';
import RouteChangeHandler from './components/RouteChangeHandler.jsx';
import Layout from './components/layout/Layout.jsx';
import PasswordReset from './pages/PasswordReset.jsx';
import FirestoreErrorHandler from './components/common/FirestoreErrorHandler.jsx';
import { checkFirestoreConnection, firestore } from './firebase';
import { doc as firestoreDoc, getDoc } from 'firebase/firestore';

// Import auth pages from /pages
import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';

// Lazy load non-critical components
const Dashboard = lazy(() => import('./components/dashboard/Dashboard.jsx'));
const WorkspaceList = lazy(() => import('./components/workspaces/WorkspaceList.jsx'));
const BasicWorkspaceView = lazy(() => import('./components/workspaces/BasicWorkspaceView.jsx'));
const DocumentEditor = lazy(() => import('./components/documents/DocumentEditor.jsx'));
const CodaDocumentEditor = lazy(() => import('./components/documents/CodaDocumentEditor.jsx'));
const DocumentList = lazy(() => import('./components/documents/DocumentList.jsx'));
const Calendar = lazy(() => import('./components/calendar/Calendar.jsx'));
const Chat = lazy(() => import('./components/chat/Chat.jsx'));
const Team = lazy(() => import('./components/team/Team.jsx'));
const Settings = lazy(() => import('./components/settings/Settings.jsx'));
const NetworkStatus = lazy(() => import('./components/common/NetworkStatus.jsx'));

// Import personal components
const PersonalDocumentList = lazy(() => import('./components/personal/PersonalDocumentList.jsx'));
const PersonalDocumentEditor = lazy(() => import('./components/personal/PersonalDocumentEditor.jsx'));
const PersonalCalendar = lazy(() => import('./components/personal/PersonalCalendar.jsx'));

// Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route error caught:', error, errorInfo);
    // Call the onError callback if provided
    if (this.props.onError && typeof this.props.onError === 'function') {
      this.props.onError(error);
    }
  }

  render() {
    // If the parent wants to handle the error (it passed an onError callback), 
    // just return children and let the parent handle rendering the error UI
    if (this.state.hasError && this.props.onError && typeof this.props.onError === 'function') {
      return null;
    }

    // Otherwise, show our default error UI
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
          <div className="bg-[#121212] p-6 rounded-xl shadow-md max-w-lg w-full border border-[#1e1e1e]">
            <h1 className="text-xl font-extralight text-red-400 mb-4">Something went wrong</h1>
            <p className="text-gray-300 mb-4">
              We've encountered an error loading this page. Please try again or return to the dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
              >
                Reload Page
              </button>
              <a 
                href="/"
                className="px-4 py-2 bg-[#1a1a1a] text-gray-300 rounded-md hover:bg-[#232323] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-center transition-colors duration-200"
              >
                Return to Dashboard
              </a>
            </div>
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4 p-3 bg-[#1a1a1a] rounded text-sm overflow-auto max-h-20">
                <p className="font-mono text-red-400">
                  {this.state.error?.toString()}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#333333]"></div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [authTimeoutElapsed, setAuthTimeoutElapsed] = useState(false);
  
  useEffect(() => {
    // Check if we have a persisted auth session flag
    const isPersisted = localStorage.getItem('bloomAuthPersisted') === 'true';
    console.log('Protected route check - Auth persisted:', isPersisted, 'Loading:', loading);
    
    // Set a timeout to handle cases where auth takes too long
    const authTimeout = setTimeout(() => {
      setAuthTimeoutElapsed(true);
      console.log('Auth timeout elapsed, continuing with routing decision');
    }, 2000); // 2 second timeout
    
    // If auth completes, clear the timeout and continue
    if (!loading) {
      console.log('Auth loading complete, user:', currentUser ? 'authenticated' : 'not authenticated');
      clearTimeout(authTimeout);
      
      // Give Firebase a short grace period to restore auth state from persistence
      // This is especially important on initial page load
      const delayBeforeAuthCheck = isPersisted ? 1000 : 300;
      
      const authCheckTimer = setTimeout(() => {
        setIsAuthenticating(false);
        console.log('Auth grace period complete, proceeding with routing');
      }, delayBeforeAuthCheck);
      
      return () => {
        clearTimeout(authCheckTimer);
      };
    }
    
    return () => {
      clearTimeout(authTimeout);
    };
  }, [loading, currentUser]);
  
  // If auth timeout elapsed and still loading, make a decision based on persisted flag
  if (authTimeoutElapsed && loading) {
    const isPersisted = localStorage.getItem('bloomAuthPersisted') === 'true';
    console.log('Auth timeout handling - isPersisted:', isPersisted);
    
    // If we have a persisted flag, assume user is authenticated and continue
    // This prevents unnecessary redirects during slow auth initialization
    if (isPersisted) {
      console.log('Auth persisted flag found, allowing protected content while auth initializes');
      return <ErrorBoundary>{children}</ErrorBoundary>;
    }
    
    // Otherwise redirect to login
    console.log('No auth persisted flag, redirecting to login due to auth timeout');
    return <Navigate to="/login" />;
  }
  
  // Still waiting for auth
  if (loading || isAuthenticating) {
    return <LoadingFallback />;
  }
  
  // No user, redirect to login
  if (!currentUser) {
    console.log('No authenticated user found, redirecting to login');
    // Clear any persistence flags when redirecting to login
    localStorage.removeItem('bloomAuthPersisted');
    return <Navigate to="/login" />;
  }
  
  // User is authenticated, render children
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

// Custom component to handle the redirect for workspace documents
const WorkspaceDocumentRedirect = () => {
  const { workspaceId, docId } = useParams();
  const [errorState, setErrorState] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Log important information about the route
  console.log('WorkspaceDocumentRedirect mounting', { workspaceId, docId, user: currentUser?.uid });

  // Check Firestore connection and document existence before rendering the document editor
  useEffect(() => {
    let isMounted = true;
    setIsChecking(true);

    const checkDocumentAccess = async () => {
      try {
        const connectionStatus = await checkFirestoreConnection();
        
        console.log('Connection check result:', connectionStatus);
        
        if (!isMounted) return;
        
        if (!connectionStatus.connected) {
          setErrorState(true);
          setErrorMessage(connectionStatus.error || 'Failed to connect to the database. Please check your internet connection.');
          setIsChecking(false);
          return;
        }
        
        // Then verify document exists
        if (workspaceId && docId) {
          console.log('Verifying document exists:', `workspaces/${workspaceId}/documents/${docId}`);
          
          // First try workspace collection
          const docRef = firestoreDoc(firestore, `workspaces/${workspaceId}/documents`, docId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            console.log('Document found in workspace collection');
          } else {
            // Try root collection as fallback
            console.log('Document not found in workspace, checking root collection');
            const rootDocRef = firestoreDoc(firestore, 'documents', docId);
            const rootDocSnap = await getDoc(rootDocRef);
            
            if (!rootDocSnap.exists()) {
              // Document doesn't exist in either location
              console.error('Document not found in either location');
              setErrorState(true);
              setErrorMessage('Document not found. It may have been deleted or you may not have permission to access it.');
            }
          }
        }
      } catch (error) {
        console.error('Error checking document access:', error);
        if (isMounted) {
          setErrorState(true);
          setErrorMessage('Error accessing document. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    checkDocumentAccess();
    
    return () => {
      isMounted = false;
    };
  }, [workspaceId, docId, currentUser]);

  if (isChecking) {
    return <LoadingFallback />;
  }

  if (errorState) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
        <div className="bg-[#121212] p-6 rounded-xl shadow-md max-w-lg w-full border border-[#1e1e1e]">
          <h1 className="text-xl font-extralight text-red-400 mb-4">Document Error</h1>
          <p className="text-gray-300 mb-4">
            {errorMessage || "We've encountered an error loading this document. Please try again or return to the workspace."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            >
              Reload Page
            </button>
            <button 
              onClick={() => navigate(`/workspaces/${workspaceId}`)}
              className="px-4 py-2 bg-[#1a1a1a] text-gray-300 rounded-md hover:bg-[#232323] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            >
              Return to Workspace
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Better error handling for workspace documents
  return (
    <ErrorBoundary
      onError={(error) => {
        console.error('WorkspaceDocumentRedirect caught error:', error);
        setErrorState(true);
        setErrorMessage(error?.message || 'Failed to load document');
      }}
    >
      <Suspense fallback={<LoadingFallback />}>
        <CodaDocumentEditor 
          workspaceId={workspaceId} 
          documentId={docId} 
          onError={(error) => {
            console.error('CodaDocumentEditor reported error:', error);
            setErrorState(true);
            setErrorMessage(error?.message || 'Failed to load document');
          }}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

const AppContent = () => {
  const { currentUser, loading } = useAuth();
  const { darkMode, lightMode } = useTheme();
  const [initialCheck, setInitialCheck] = useState(true);
  
  // Check for persisted authentication on mount
  useEffect(() => {
    const isPersisted = localStorage.getItem('bloomAuthPersisted') === 'true';
    
    // If we're still loading but have a persisted flag, don't redirect
    if (loading && isPersisted) {
      console.log('AppContent: Auth still loading but persisted flag found');
      setInitialCheck(false);
      return;
    }
    
    // If we're done loading, check auth state
    if (!loading) {
      console.log('AppContent: Auth loading complete, user:', currentUser ? 'authenticated' : 'not authenticated');
      setInitialCheck(false);
    }
  }, [loading, currentUser]);

  // Only redirect if initial check is complete and no user is found
  if (!initialCheck && !currentUser && !loading) {
    console.log('AppContent: No authenticated user found after check, redirecting to login');
    return <Navigate to="/login" />;
  }
  
  // Show loading state while performing initial check
  if (initialCheck && loading) {
    return <LoadingFallback />;
  }

  // Apply both dark and light mode classes as needed
  const themeClasses = [];
  if (darkMode) themeClasses.push('dark');
  if (lightMode) themeClasses.push('light');

  return (
    <div className={themeClasses.join(' ')}>
      <div className={`min-h-screen bg-white dark:bg-darkgray-900 text-gray-900 dark:text-white ${lightMode ? 'light-mode-active' : ''}`}>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="documents" element={<DocumentList />} />
            
            {/* Legacy document editor */}
            <Route path="documents/legacy/:id" element={<DocumentEditor />} />
            
            {/* New Coda-like document editor */}
            <Route path="documents/new" element={<CodaDocumentEditor />} />
            <Route path="documents/:id" element={<CodaDocumentEditor />} />
              
            {/* Personal section routes */}
            <Route path="personal/documents" element={<PersonalDocumentList />} />
            <Route path="personal/documents/:id" element={<PersonalDocumentEditor />} />
            <Route path="personal/calendar" element={<PersonalCalendar />} />
            
            <Route path="workspaces" element={<WorkspaceList />} />
            <Route path="workspaces/:id" element={<BasicWorkspaceView />} />
              
            {/* Handle workspace documents through a dedicated route */}
            <Route 
              path="workspaces/:workspaceId/documents/:docId" 
              element={<WorkspaceDocumentRedirect />} 
            />
              
            <Route path="team" element={<Team />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="settings" element={<Settings />} />
            <Route path="chat" element={<Chat />} />
          </Route>
            
          {/* 404 route */}
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center bg-black">
              <div className="bg-[#121212] p-8 rounded-xl shadow-md max-w-md w-full text-center border border-[#1e1e1e]">
                <h1 className="text-4xl font-extralight text-white mb-4 tracking-wide">404</h1>
                <p className="text-lg text-gray-300 mb-6">Page not found</p>
                <a 
                  href="/"
                  className="px-5 py-3 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333333] inline-block transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Go to Dashboard
                </a>
              </div>
            </div>
          } />
        </Routes>
      </div>
    </div>
  );
};

function App() {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <ThemeProvider>
        <AuthProvider>
          <NotificationsProvider>
            <Router>
              <RouteChangeHandler />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<PasswordReset />} />
                  <Route path="/*" element={<AppContent />} />
                </Routes>
              </Suspense>
            </Router>
            <FirestoreErrorHandler />
          </NotificationsProvider>
        </AuthProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
}

export default App;
